import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mezzi, attrezzature, organizzazioni } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Truck, Plus, Edit, Trash2, Wrench, Fuel, MapPin, Package } from "lucide-react";
import { toast } from "sonner";

const STATI_MEZZO = [
  { value: "disponibile", label: "Disponibile", color: "bg-green-100 text-green-700" },
  { value: "in_uso", label: "In Uso", color: "bg-blue-100 text-blue-700" },
  { value: "manutenzione", label: "Manutenzione", color: "bg-yellow-100 text-yellow-700" },
  { value: "fuori_servizio", label: "Fuori Servizio", color: "bg-red-100 text-red-700" },
];

const TIPI_MEZZO = [
  "Ambulanza", "Autocarro", "Autovettura", "Fuoristrada", "Furgone", "Pulmino", "Rimorchio", "Altro"
];

const CATEGORIE_ATTREZZATURA = [
  "Comunicazioni", "Primo Soccorso", "Logistica", "Elettricità", "Idraulica", "Antincendio", "Altro"
];

export default function Mezzi() {
  const [mezzoDialogOpen, setMezzoDialogOpen] = useState(false);
  const [attrDialogOpen, setAttrDialogOpen] = useState(false);
  const [selectedMezzo, setSelectedMezzo] = useState(null);
  const [selectedAttr, setSelectedAttr] = useState(null);
  const [mezzoForm, setMezzoForm] = useState({
    targa: "", tipo: "Autovettura", modello: "", stato: "disponibile",
    carburante: 100, km_attuali: 0, posizione: "", note: "",
    organizzazione_id: "", organizzazione_nome: ""
  });
  const [attrForm, setAttrForm] = useState({
    codice: "", nome: "", categoria: "Logistica", stato: "disponibile", posizione: "", note: ""
  });

  const queryClient = useQueryClient();

  const { data: mezziData } = useQuery({ queryKey: ['mezzi'], queryFn: mezzi.list });
  const { data: attrData } = useQuery({ queryKey: ['attrezzature'], queryFn: attrezzature.list });
  const { data: orgData } = useQuery({ queryKey: ['organizzazioni'], queryFn: organizzazioni.list });

  const createMezzoMutation = useMutation({
    mutationFn: mezzi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['mezzi']);
      setMezzoDialogOpen(false);
      resetMezzoForm();
      toast.success("Veicolo aggiunto");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMezzoMutation = useMutation({
    mutationFn: ({ id, data }) => mezzi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['mezzi']);
      setMezzoDialogOpen(false);
      setSelectedMezzo(null);
      toast.success("Veicolo aggiornato");
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMezzoMutation = useMutation({
    mutationFn: mezzi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['mezzi']);
      toast.success("Veicolo eliminato");
    },
  });

  const createAttrMutation = useMutation({
    mutationFn: attrezzature.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['attrezzature']);
      setAttrDialogOpen(false);
      resetAttrForm();
      toast.success("Attrezzatura aggiunta");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateAttrMutation = useMutation({
    mutationFn: ({ id, data }) => attrezzature.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['attrezzature']);
      setAttrDialogOpen(false);
      setSelectedAttr(null);
      toast.success("Attrezzatura aggiornata");
    },
  });

  const deleteAttrMutation = useMutation({
    mutationFn: attrezzature.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['attrezzature']);
      toast.success("Attrezzatura eliminata");
    },
  });

  const resetMezzoForm = () => setMezzoForm({
    targa: "", tipo: "Autovettura", modello: "", stato: "disponibile",
    carburante: 100, km_attuali: 0, posizione: "", note: "",
    organizzazione_id: "", organizzazione_nome: ""
  });

  const resetAttrForm = () => setAttrForm({
    codice: "", nome: "", categoria: "Logistica", stato: "disponibile", posizione: "", note: ""
  });

  const handleEditMezzo = (m) => {
    setSelectedMezzo(m);
    setMezzoForm({ ...m });
    setMezzoDialogOpen(true);
  };

  const handleEditAttr = (a) => {
    setSelectedAttr(a);
    setAttrForm({ ...a });
    setAttrDialogOpen(true);
  };

  const handleSubmitMezzo = (e) => {
    e.preventDefault();
    if (selectedMezzo) {
      updateMezzoMutation.mutate({ id: selectedMezzo.id, data: mezzoForm });
    } else {
      createMezzoMutation.mutate(mezzoForm);
    }
  };

  const handleSubmitAttr = (e) => {
    e.preventDefault();
    if (selectedAttr) {
      updateAttrMutation.mutate({ id: selectedAttr.id, data: attrForm });
    } else {
      createAttrMutation.mutate(attrForm);
    }
  };

  const getStatoColor = (stato) => STATI_MEZZO.find(s => s.value === stato)?.color || "bg-slate-100 text-slate-700";
  const getFuelColor = (level) => level > 50 ? "bg-green-500" : level > 20 ? "bg-yellow-500" : "bg-red-500";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mezzi-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Veicoli e Attrezzature</h1>
        <p className="text-slate-500 mt-1">Gestione parco veicoli e inventario attrezzature</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg"><Truck className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{mezziData?.length || 0}</p>
                <p className="text-xs text-slate-500">Veicoli Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg"><Truck className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold">{mezziData?.filter(m => m.stato === "disponibile").length || 0}</p>
                <p className="text-xs text-slate-500">Disponibili</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg"><Package className="h-5 w-5 text-purple-600" /></div>
              <div>
                <p className="text-2xl font-bold">{attrData?.length || 0}</p>
                <p className="text-xs text-slate-500">Attrezzature</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg"><Wrench className="h-5 w-5 text-yellow-600" /></div>
              <div>
                <p className="text-2xl font-bold">{mezziData?.filter(m => m.stato === "manutenzione").length || 0}</p>
                <p className="text-xs text-slate-500">In Manutenzione</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="mezzi" className="space-y-4">
        <TabsList>
          <TabsTrigger value="mezzi">Parco Veicoli</TabsTrigger>
          <TabsTrigger value="attrezzature">Inventario Attrezzature</TabsTrigger>
        </TabsList>

        {/* Mezzi Tab */}
        <TabsContent value="mezzi" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={mezzoDialogOpen} onOpenChange={(open) => {
              setMezzoDialogOpen(open);
              if (!open) { setSelectedMezzo(null); resetMezzoForm(); }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-mezzo-btn">
                  <Plus className="h-4 w-4 mr-2" />Nuovo Veicolo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{selectedMezzo ? "Modifica Veicolo" : "Nuovo Veicolo"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitMezzo} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Targa *</Label>
                      <Input value={mezzoForm.targa} onChange={(e) => setMezzoForm({ ...mezzoForm, targa: e.target.value.toUpperCase() })} required data-testid="input-targa" />
                    </div>
                    <div>
                      <Label>Tipo</Label>
                      <Select value={mezzoForm.tipo} onValueChange={(v) => setMezzoForm({ ...mezzoForm, tipo: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIPI_MEZZO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Modello</Label>
                    <Input value={mezzoForm.modello} onChange={(e) => setMezzoForm({ ...mezzoForm, modello: e.target.value })} />
                  </div>
              <div>
                <Label>Organizzazione</Label>
                <Select value={mezzoForm.organizzazione_id || "__altra__"} onValueChange={(v) => {
                  if (v === "__altra__") {
                    setMezzoForm({ ...mezzoForm, organizzazione_id: "", organizzazione_nome: "Altra" });
                  } else {
                    const org = (orgData || []).find(o => o.id === v);
                    setMezzoForm({ ...mezzoForm, organizzazione_id: v, organizzazione_nome: org ? org.nome : "" });
                  }
                }}>
                  <SelectTrigger data-testid="select-organizzazione"><SelectValue placeholder="Seleziona organizzazione" /></SelectTrigger>
                  <SelectContent>
                    {(orgData || []).map(o => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}
                    <SelectItem value="__altra__">Altra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setMezzoDialogOpen(false)}>Annulla</Button>
                    <Button type="submit" className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="submit-mezzo-btn">Salva</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mezzi-list">
            {mezziData?.map(m => (
              <Card key={m.id} data-testid={`mezzo-card-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg"><Truck className="h-6 w-6 text-blue-600" /></div>
                      <div>
                        <h3 className="font-bold font-mono">{m.targa}</h3>
                        <p className="text-sm text-slate-500">{m.tipo} {m.modello && `- ${m.modello}`}</p>
                      {m.organizzazione_nome && <p className="text-xs text-slate-400 truncate" title={m.organizzazione_nome}>{m.organizzazione_nome}</p>}
                      </div>
                    </div>
                    <Badge className={getStatoColor(m.stato)}>{STATI_MEZZO.find(s => s.value === m.stato)?.label}</Badge>
                  </div>
                  <div className="mt-4 space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="flex items-center gap-1"><Fuel className="h-3 w-3" /> Carburante</span>
                        <span>{m.carburante}%</span>
                      </div>
                      <Progress value={m.carburante} className={`h-2 ${getFuelColor(m.carburante)}`} />
                    </div>
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>KM: {m.km_attuali?.toLocaleString()}</span>
                      {m.posizione && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{m.posizione}</span>}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEditMezzo(m)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { if (window.confirm("Eliminare?")) deleteMezzoMutation.mutate(m.id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Attrezzature Tab */}
        <TabsContent value="attrezzature" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={attrDialogOpen} onOpenChange={(open) => {
              setAttrDialogOpen(open);
              if (!open) { setSelectedAttr(null); resetAttrForm(); }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-attr-btn">
                  <Plus className="h-4 w-4 mr-2" />Nuova Attrezzatura
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{selectedAttr ? "Modifica Attrezzatura" : "Nuova Attrezzatura"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitAttr} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Codice *</Label>
                      <Input value={attrForm.codice} onChange={(e) => setAttrForm({ ...attrForm, codice: e.target.value.toUpperCase() })} required data-testid="input-attr-codice" />
                    </div>
                    <div>
                      <Label>Categoria</Label>
                      <Select value={attrForm.categoria} onValueChange={(v) => setAttrForm({ ...attrForm, categoria: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIE_ATTREZZATURA.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Nome *</Label>
                    <Input value={attrForm.nome} onChange={(e) => setAttrForm({ ...attrForm, nome: e.target.value })} required data-testid="input-attr-nome" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Stato</Label>
                      <Select value={attrForm.stato} onValueChange={(v) => setAttrForm({ ...attrForm, stato: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disponibile">Disponibile</SelectItem>
                          <SelectItem value="in_uso">In Uso</SelectItem>
                          <SelectItem value="manutenzione">Manutenzione</SelectItem>
                          <SelectItem value="guasto">Guasto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Posizione</Label>
                      <Input value={attrForm.posizione} onChange={(e) => setAttrForm({ ...attrForm, posizione: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAttrDialogOpen(false)}>Annulla</Button>
                    <Button type="submit" className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="submit-attr-btn">Salva</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="attr-list">
            {attrData?.map(a => (
              <Card key={a.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-50 rounded-lg"><Package className="h-6 w-6 text-purple-600" /></div>
                      <div>
                        <h3 className="font-semibold">{a.nome}</h3>
                        <p className="text-xs text-slate-500 font-mono">{a.codice}</p>
                      </div>
                    </div>
                    <Badge variant="outline">{a.categoria}</Badge>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <Badge className={a.stato === "disponibile" ? "bg-green-100 text-green-700" : a.stato === "guasto" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}>
                      {a.stato}
                    </Badge>
                    {a.posizione && <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{a.posizione}</span>}
                  </div>
                  <div className="mt-3 flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEditAttr(a)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { if (window.confirm("Eliminare?")) deleteAttrMutation.mutate(a.id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
